import {
  Component,
  EventEmitter,
  Input,
  Output,
  computed,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { MaterialModule } from '@app/material.module';

/**
 * Una tarjeta de la barra de indicadores.
 *
 * `key` es lo que se usa para filtrar, no la etiqueta: la etiqueta cambia
 * según el módulo ("Activos" en equipo, "Disponibles" en instrumental) pero
 * el filtro que aplica es el mismo.
 */
export interface InventoryKpi {
  key: string;
  label: string;
  icon: string;
  value: number;
  /** `total` no filtra: al hacer clic quita cualquier filtro activo. */
  isTotal?: boolean;
  /** Tono de la tarjeta. Por defecto `neutral`. */
  tone?: 'neutral' | 'ok' | 'warn' | 'danger';
}

export interface InventoryScope {
  place: string;
  warehouse: string;
}

/**
 * Barra superior compartida por los tres módulos de inventario
 * (productos, equipo e instrumental).
 *
 * Junta las tres cosas que los tres necesitan igual:
 *
 *   1. El selector SITIO → ALMACÉN. Los almacenes se acotan al sitio elegido
 *      (`warehouses` trae todos y aquí se filtra por `place`), y mientras
 *      falte cualquiera de los dos el componente NO deja avanzar: emite
 *      `scopeChange` con el almacén vacío y el módulo anfitrión se queda sin
 *      pedir datos. Esto es a propósito: el inventario completo de todos los
 *      almacenes es una consulta cara y una tabla que nadie lee.
 *
 *   2. La barra de indicadores. Son botones, no adornos: al hacer clic se
 *      emite `kpiToggle` con su `key` y el módulo filtra la tabla o las
 *      tarjetas. Volver a hacer clic en el mismo, o hacer clic en el total,
 *      quita el filtro.
 *
 *   3. El cambio de vista tabla ↔ tarjetas, que se recuerda por módulo en
 *      `localStorage` bajo `storageKey`.
 *
 * El componente no sabe nada de stock: no llama servicios ni conoce los
 * estados de cada módulo. Recibe listas y emite eventos; quién carga y qué
 * significa cada KPI lo decide cada módulo.
 */
@Component({
  selector: 'app-inventory-toolbar',
  standalone: true,
  imports: [NgClass, MaterialModule],
  template: `
    <!-- ── Alcance: sitio → locker ─────────────────────────────── -->
    <!-- Se puede ocultar con showScope=false cuando el módulo ya trae su
         propio selector; en ese caso el módulo manda el estado por
         scopeReadyInput y aquí solo quedan los indicadores y la vista. -->
    @if (showScope) {
    <div class="inv-scope">
      <mat-form-field appearance="outline" class="inv-scope__field">
        <mat-label>Ubicación</mat-label>
        <mat-select [value]="place()" (selectionChange)="onPlace($event.value)">
          @for (p of hospitalPlaces; track p.id) {
            <mat-option [value]="p.id">{{ p.alias || p.name }}</mat-option>
          }
        </mat-select>
      </mat-form-field>

      @if (useWarehouse) {
      <mat-icon class="inv-scope__arrow">chevron_right</mat-icon>

      <mat-form-field appearance="outline" class="inv-scope__field">
        <mat-label>Locker</mat-label>
        <mat-select
          [value]="warehouse()"
          [disabled]="!place()"
          (selectionChange)="onWarehouse($event.value)">
          @for (w of warehousesOfPlace(); track w.id) {
            <mat-option [value]="w.id">{{ w.name }}</mat-option>
          }
        </mat-select>
        @if (place() && warehousesOfPlace().length === 0) {
          <mat-hint>Esta ubicación no tiene lockers dados de alta.</mat-hint>
        }
      </mat-form-field>
      }

      @if (ready()) {
        <button mat-button class="inv-scope__clear" (click)="clear()">
          <mat-icon>filter_alt_off</mat-icon>
          {{ useWarehouse ? 'Cambiar locker' : 'Cambiar ubicación' }}
        </button>
      }

      <span class="inv-scope__spacer"></span>

      <!-- Vista: tabla o tarjetas -->
      @if (ready() && showViewToggle) {
        <div class="inv-view">
          <button
            mat-icon-button
            [class.inv-view--on]="viewMode === 'table'"
            matTooltip="Ver como tabla"
            (click)="setView('table')">
            <mat-icon>table_rows</mat-icon>
          </button>
          <button
            mat-icon-button
            [class.inv-view--on]="viewMode === 'cards'"
            matTooltip="Ver como tarjetas"
            (click)="setView('cards')">
            <mat-icon>grid_view</mat-icon>
          </button>
        </div>
      }
    </div>
    }

    <!-- La vista tabla/tarjetas también hace falta cuando el selector se
         oculta, así que se repite fuera del bloque de arriba. -->
    @if (!showScope && ready() && showViewToggle) {
      <div class="inv-view inv-view--standalone">
        <button mat-icon-button [class.inv-view--on]="viewMode === 'table'"
                matTooltip="Ver como tabla" (click)="setView('table')">
          <mat-icon>table_rows</mat-icon>
        </button>
        <button mat-icon-button [class.inv-view--on]="viewMode === 'cards'"
                matTooltip="Ver como tarjetas" (click)="setView('cards')">
          <mat-icon>grid_view</mat-icon>
        </button>
      </div>
    }

    <!-- ── Indicadores ──────────────────────────────────────────── -->
    @if (ready() && kpis.length > 0) {
      <div class="inv-kpis">
        @for (k of kpis; track k.key) {
          <button
            class="inv-kpi"
            [class.inv-kpi--active]="activeKpi === k.key"
            [ngClass]="'inv-kpi--' + (k.tone ?? 'neutral')"
            [matTooltip]="k.isTotal
              ? 'Quitar el filtro y ver todo'
              : 'Filtrar por ' + k.label.toLowerCase()"
            (click)="onKpi(k)">
            <mat-icon class="inv-kpi__icon">{{ k.icon }}</mat-icon>
            <span class="inv-kpi__value">{{ k.value }}</span>
            <span class="inv-kpi__label">{{ k.label }}</span>
          </button>
        }
      </div>
    }

    <!-- ── Sin alcance: no se pide nada al API ──────────────────── -->
    @if (!ready()) {
      <div class="inv-empty">
        <mat-icon class="inv-empty__icon">warehouse</mat-icon>
        <h3>Selecciona una ubicación y un locker</h3>
        <p>
          El inventario se carga por locker. Elige arriba la ubicación y luego el
          locker para ver sus existencias.
        </p>
      </div>
    }
  `,
  styles: [`
    :host { display: block; }

    .inv-scope {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
      padding: 4px 0 0;
    }
    .inv-scope__field { min-width: 220px; }
    .inv-scope__arrow { color: #999999; }
    .inv-scope__spacer { flex: 1 1 auto; }
    .inv-scope__clear { color: #555555; }

    .inv-view {
      display: inline-flex;
      border: 1px solid #e8e8e8;
      border-radius: 10px;
      overflow: hidden;
      background: #ffffff;
    }
    .inv-view--standalone { margin: 4px 0 0; }
    .inv-view--on {
      background: rgba(40, 94, 255, 0.10);
      color: #1010eb;
    }

    /* ── Indicadores ───────────────────────────────────────────── */
    .inv-kpis {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
      gap: 12px;
      margin: 16px 0 20px;
    }

    .inv-kpi {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 14px 16px;
      background: #ffffff;
      border: 1px solid #e8e8e8;
      border-radius: 12px;
      cursor: pointer;
      text-align: left;
      font: inherit;
      transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease;
    }
    .inv-kpi:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 18px rgba(0,0,0,.08);
    }
    /* El activo se marca con borde y un fondo tenue: se ve cuál está
       filtrando sin tener que leer la tabla. */
    .inv-kpi--active {
      border-color: #1010eb;
      background: rgba(40, 94, 255, 0.06);
      box-shadow: inset 0 0 0 1px #1010eb;
    }

    .inv-kpi__icon {
      flex: 0 0 auto;
      width: 34px; height: 34px; font-size: 20px; line-height: 34px;
      display: inline-flex; align-items: center; justify-content: center;
      border-radius: 10px;
      background: #f4f4f4;
      color: #170f5f;
    }
    .inv-kpi--ok     .inv-kpi__icon { background: rgba(20, 133, 61,.10);   color: #14853d; }
    .inv-kpi--warn   .inv-kpi__icon { background: rgba(207, 80, 9,.14); color: #cf5009; }
    .inv-kpi--danger .inv-kpi__icon { background: rgba(16, 16, 235,.10);  color: #1010eb; }

    .inv-kpi__value {
      font-size: 1.35rem; font-weight: 700; color: #170f5f; line-height: 1;
    }
    .inv-kpi__label {
      font-size: .8rem; color: #555555; line-height: 1.2;
    }

    /* ── Estado vacío ──────────────────────────────────────────── */
    .inv-empty {
      display: flex; flex-direction: column; align-items: center;
      text-align: center; padding: 56px 24px; max-width: 460px; margin: 0 auto;
      color: #555555;
    }
    .inv-empty__icon {
      font-size: 52px; width: 52px; height: 52px; margin-bottom: 12px;
      color: #bfbfbf;
    }
    .inv-empty h3 {
      margin: 0 0 6px; font-size: 1.1rem; font-weight: 700; color: #170f5f;
    }
    .inv-empty p { margin: 0; font-size: .9rem; line-height: 1.5; }

    @media (max-width: 768px) {
      .inv-scope__field { min-width: 100%; }
      .inv-scope__arrow { display: none; }
    }
  `],
})
export class InventoryToolbarComponent {

  /** Catálogo de sitios. */
  @Input() places: any[] = [];

  /** En los inventarios solo se eligen ubicaciones tipo HOSPITAL (por alias). */
  get hospitalPlaces(): any[] {
    return (this.places ?? [])
      .filter(p => String(p?.type ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase() === 'HOSPITAL')
      .sort((a, b) => String(a.alias || a.name || '').localeCompare(String(b.alias || b.name || ''), 'es', { sensitivity: 'base', numeric: true }));
  }

  /** TODOS los almacenes; aquí se filtran por el sitio elegido. */
  @Input() warehouses: any[] = [];

  @Input() kpis: InventoryKpi[] = [];

  /** `key` del KPI que está filtrando, o null. */
  @Input() activeKpi: string | null = null;

  @Input() viewMode: 'table' | 'cards' = 'table';

  /**
   * Sufijo con el que se recuerda la vista elegida, por módulo
   * (`inventoryViewMode:equipments`, `:products`, `:instrumentals`).
   */
  @Input() storageKey = 'default';

  /** `false` = el módulo pone el cambio de vista en su barra de filtros. */
  @Input() showViewToggle = true;

  /** `false` = solo se elige la ubicación (hospital), sin locker (p. ej. equipo). */
  @Input() useWarehouse = true;

  /** `false` cuando el módulo ya tiene su propio selector de sitio/almacén. */
  @Input() showScope = true;

  /**
   * Estado del alcance cuando lo administra el módulo (`showScope=false`).
   * Se ignora si el selector es de este componente.
   */
  @Input() scopeReadyInput = false;

  @Output() scopeChange = new EventEmitter<InventoryScope>();
  @Output() kpiToggle = new EventEmitter<string | null>();
  @Output() viewModeChange = new EventEmitter<'table' | 'cards'>();

  readonly place = signal<string>('');
  readonly warehouse = signal<string>('');

  /**
   * Solo con sitio Y almacén se considera que hay alcance.
   * Si el selector no es de aquí, manda lo que diga el módulo.
   */
  readonly ready = computed(() =>
    this.showScope ? (!!this.place() && (!this.useWarehouse || !!this.warehouse())) : this.scopeReadyInput
  );

  readonly warehousesOfPlace = computed(() => {
    const p = this.place();
    if (!p) return [];
    return (this.warehouses ?? []).filter(w => w.place === p);
  });

  onPlace(placeId: string): void {
    this.place.set(placeId ?? '');
    // Cambiar de sitio invalida el almacén: el que estaba elegido pertenece
    // al sitio anterior y dejaría cargando datos de otro lado.
    this.warehouse.set('');
    this.emitScope();
  }

  onWarehouse(warehouseId: string): void {
    this.warehouse.set(warehouseId ?? '');
    this.emitScope();
  }

  clear(): void {
    this.place.set('');
    this.warehouse.set('');
    this.emitScope();
  }

  private emitScope(): void {
    this.scopeChange.emit({ place: this.place(), warehouse: this.warehouse() });
  }

  onKpi(k: InventoryKpi): void {
    // El total no es un filtro, es el "ver todo".
    if (k.isTotal) { this.kpiToggle.emit(null); return; }
    // Volver a hacer clic en el que ya está activo lo apaga.
    this.kpiToggle.emit(this.activeKpi === k.key ? null : k.key);
  }

  setView(mode: 'table' | 'cards'): void {
    this.viewMode = mode;
    try {
      localStorage.setItem(`inventoryViewMode:${this.storageKey}`, mode);
    } catch { /* almacenamiento no disponible: no es crítico */ }
    this.viewModeChange.emit(mode);
  }

  /** Recuerda la vista elegida (cuando el cambio de vista vive en el módulo). */
  static storeView(storageKey: string, mode: 'table' | 'cards'): void {
    try { localStorage.setItem(`inventoryViewMode:${storageKey}`, mode); } catch { /* no crítico */ }
  }

  /** Lee la vista recordada. La llama el módulo anfitrión en su ngOnInit. */
  static readStoredView(storageKey: string): 'table' | 'cards' {
    try {
      const v = localStorage.getItem(`inventoryViewMode:${storageKey}`);
      return v === 'cards' ? 'cards' : 'table';
    } catch {
      return 'table';
    }
  }
}
